import { Link } from "react-router";

import { GuideArticle, Section } from "../GuideShell";

export function AssessmentMatrixPage() {
  return (
    <GuideArticle
      slug="assessment-matrix"
      title="Assessment matrix"
      intro={
        <p>
          The assessment matrix is a per-dimension calibration aid: for each capability dimension on
          the role blueprint, it spells out what evidence looks like at each band. It exists so that
          two different assessors looking at the same candidate land on the same score for the same
          reasons.
        </p>
      }
    >
      <Section id="what" title="What the matrix defines">
        <p>
          For each capability dimension on the{" "}
          <Link to="/help/role-blueprint" className="text-link hover:underline">
            blueprint
          </Link>
          , the matrix carries three bands of evidence criteria:
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong>4–5 band:</strong> what strongly-meets or standout evidence looks like for this
            dimension at this role's required level.
          </li>
          <li>
            <strong>3 band:</strong> what baseline-meeting evidence looks like — the hire-standard
            line.
          </li>
          <li>
            <strong>1–2 band:</strong> what not-meeting or partially-meeting evidence looks like,
            including the gap that takes the candidate below the bar.
          </li>
        </ul>
        <p>
          These bands are written specifically for the required level on the blueprint. A "strongly
          meets" entry on a level-8 Leadership dimension reads very differently from a "strongly
          meets" entry on a level-5 Leadership dimension.
        </p>
      </Section>

      <Section id="purpose" title="Why three bands, not five">
        <p>
          The matrix bands the 1–5 evaluation scale into three groups (1–2 / 3 / 4–5) because the
          empirical distinction assessors can reliably make from evidence is "below bar / at bar /
          above bar". Asking writers to distinguish a 4 from a 5 a priori, in band copy, leads to
          generic prose; assessors are better at making that finer call against the actual
          candidate's evidence than at consuming pre-written distinctions.
        </p>
      </Section>

      <Section id="how-used" title="How the matrix is used">
        <p>
          Assessors consult the matrix while scoring: pick the band whose description best matches
          the available evidence on this dimension for this candidate, then sharpen to a specific
          1–5 score. The anchor on that score should be supportable from the behavioural-anchor rows
          attached to the score — see{" "}
          <Link to="/help/anchors" className="text-link hover:underline">
            behavioural anchors
          </Link>
          .
        </p>
      </Section>

      <Section id="locking" title="Locking and origin">
        <p>
          The matrix is generated from the blueprint and is regeneratable. Once a recruiter starts
          assessing candidates against it, the matrix is typically locked — regenerating mid-search
          breaks comparability across the candidates already scored. The lock toggle and the
          system/user origin stamp work the same way as on the blueprint.
        </p>
      </Section>
    </GuideArticle>
  );
}
